/**
 * Meta Pixel — o mesmo pixel do site institucional (mercadounico.tech).
 *
 * Os dois domínios dividem o `.mercadounico.tech`, e é isso que faz a
 * atribuição funcionar: o pixel do site grava o clique do anúncio (`fbclid`)
 * no cookie `_fbc` do domínio raiz, e o daqui o lê de volta quando a conta
 * nasce — mesmo que o cadastro atravesse dias e volte pelo link do WhatsApp,
 * que só carrega o `?cnpj=`.
 *
 * Sem `VITE_META_PIXEL_ID` tudo aqui vira no-op silencioso, como o PostHog.
 *
 * **PageView das trocas de rota é do próprio pixel**: ele escuta o
 * `history.pushState` do React Router sozinho. Disparar outro aqui contaria
 * cada navegação duas vezes.
 */

interface Fbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/** Correspondência avançada — o pixel normaliza e aplica SHA-256 antes de enviar. */
export interface MetaUserData {
  ph?: string;
  em?: string;
}

let pixelId = '';

/** O snippet oficial da Meta, tipado: fila até o `fbevents.js` carregar. */
function installStub(): Fbq {
  if (window.fbq) return window.fbq;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  return fbq;
}

export function initMetaPixel(): void {
  const id = import.meta.env.VITE_META_PIXEL_ID;
  if (!id || pixelId || typeof window === 'undefined') return;
  const fbq = installStub();
  fbq('init', id);
  fbq('track', 'PageView');
  pixelId = id;
}

/**
 * Evento padrão com `eventID` — é o que a Meta usa para casar o disparo do
 * navegador com o da Conversions API e contar a conversão uma vez só.
 *
 * Os dados pessoais entram por um segundo `init` do mesmo pixel, que é como a
 * Meta recebe correspondência avançada conhecida só depois do carregamento.
 * É a exceção deliberada à regra "nunca PII" de `analytics.ts`: aqui o dado
 * sai com hash, e só para o pixel de anúncio.
 */
export function metaTrack(
  event: 'Lead' | 'CompleteRegistration',
  eventId: string,
  userData?: MetaUserData
): boolean {
  if (!pixelId || !window.fbq) return false;
  if (userData && (userData.ph || userData.em)) {
    window.fbq('init', pixelId, userData);
  }
  window.fbq('track', event, {}, { eventID: eventId });
  return true;
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const found = document.cookie
    .split('; ')
    .find((part) => part.startsWith(prefix));
  return found ? decodeURIComponent(found.slice(prefix.length)) : null;
}

/**
 * Os identificadores do navegador para a Conversions API. Sem eles o evento do
 * servidor não se liga ao clique no anúncio — o `_fbc` é o clique, o `_fbp` é
 * o navegador.
 */
export function metaBrowserIds(): { fbp: string | null; fbc: string | null } {
  if (!pixelId) return { fbp: null, fbc: null };
  return { fbp: readCookie('_fbp'), fbc: readCookie('_fbc') };
}
